import React, { useState } from 'react';
import { Wand2, Loader } from 'lucide-react';
import { FormField } from '../../../types/formBuilder';

interface TextareaInputProps {
  field: FormField;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
}

export const TextareaInput: React.FC<TextareaInputProps> = ({
  field,
  value,
  onChange,
  error,
  onFocus,
  onBlur,
  disabled = false
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState<string | null>(null);

  const baseClasses = "w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all resize-none";
  const errorClasses = error ? "border-status-error focus:border-status-error" : "";

  const handleEnhancePrompt = async () => {
    if (!value?.trim()) {
      setEnhanceError('Please enter some text first');
      return;
    }

    if (!field.systemInstructions?.trim()) {
      setEnhanceError('System instructions not configured');
      return;
    }

    const openAIKey = import.meta.env.VITE_OPENAI_KEY;
    if (!openAIKey) {
      setEnhanceError('OpenAI API key not configured');
      return;
    }

    setIsEnhancing(true);
    setEnhanceError(null);

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openAIKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            {
              role: 'system',
              content: field.systemInstructions
            },
            {
              role: 'user',
              content: `Please enhance this prompt: "${value}"`
            }
          ],
          temperature: 0.7,
          max_tokens: 500
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`OpenAI API error: ${response.status} - ${errorData.error?.message || 'Request failed'}`);
      }

      const data = await response.json();
      const enhancedPrompt = data.choices[0]?.message?.content;

      if (!enhancedPrompt) {
        throw new Error('No enhanced prompt received from OpenAI');
      }

      // Replace the current value with the enhanced prompt
      onChange(enhancedPrompt.trim());
      
    } catch (error) {
      console.error('Prompt enhancement error:', error);
      setEnhanceError(error instanceof Error ? error.message : 'Enhancement failed');
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <div className="space-y-3">
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={field.placeholder}
        required={field.required}
        disabled={disabled || isEnhancing}
        rows={field.rows || 4}
        className={`${baseClasses} ${errorClasses}`}
      />
      
      {/* Enhance Button - Moved below textarea for better visibility */}
      {field.enablePromptEnhancement && (
        <button
          type="button"
          onClick={handleEnhancePrompt}
          disabled={disabled || isEnhancing || !value?.trim()}
          className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-brand text-surface rounded-xl font-semibold hover:scale-105 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand/30"
          title="Enhance your prompt with AI for better results"
        >
          {isEnhancing ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span className="text-lg">Enhancing Prompt...</span>
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              <span className="text-lg">✨ Enhance Prompt with AI</span>
            </>
          )}
        </button>
      )}
      
      {/* Enhancement Error */}
      {enhanceError && (
        <p className="text-status-error text-xs flex items-center space-x-1">
          <span>⚠️</span>
          <span>{enhanceError}</span>
        </p>
      )}
      
      {/* Enhancement Info */}
      {field.enablePromptEnhancement && !enhanceError && !value?.trim() && (
        <p className="text-brand text-xs flex items-center space-x-1">
          <Wand2 className="w-3 h-3" />
          <span>Write your prompt above, then use AI enhancement for better results</span>
        </p>
      )}
    </div>
  );
};